"use strict";
// Orders list + Order Detail — both real, live reads from Shopify via
// GET /me/orders[/:id] (FlixAuthClient, already handles 401->refresh
// transparently). No status is ever invented: financialStatus/
// fulfillmentStatus are Shopify's own real values, passed through exactly
// as flix-identity returns them (ADR 015) — only their *display* (badge
// color/label) is a frontend decision, per this phase's own instruction.
//
// Purchases appear by themselves: Flix Identity links the account to the
// Shopify customer its verified email already has the first time orders
// are asked for (the owner's decision of 2026-10-26 — no claim button), so
// an empty list here is simply an account with no orders yet.
(function () {
    var bound = false;
    var ORDER_PARAM = "order";
    var PAGE_LIMIT = 10;
    var TONE_STYLE = {
        success: "background:color-mix(in srgb, var(--success) 14%, transparent);border:1px solid color-mix(in srgb, var(--success) 40%, transparent);color:var(--success)",
        warning: "background:rgba(245,166,35,.14);border:1px solid rgba(245,166,35,.4);color:var(--warning)",
        error: "background:rgba(224,86,74,.14);border:1px solid rgba(224,86,74,.4);color:var(--error)",
        neutral: "background:var(--surface2);border:1px solid var(--border);color:var(--dim)",
    };
    var PACKAGE_ICON = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><path d="M3.3 7 12 12l8.7-5"/><path d="M12 22V12"/></svg>';
    var ARROW_BACK_ICON = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>';
    function t(hubEl, key, fallback) {
        return hubEl.dataset[key] || fallback;
    }
    function orderIdFromLocation() {
        return new URLSearchParams(window.location.search).get(ORDER_PARAM);
    }
    /**
     * Shopify's real displayFinancialStatus/displayFulfillmentStatus enum
     * values (documented at shopify.dev), sorted into a badge tone — a
     * frontend presentation choice, not a new status value; anything
     * unrecognized safely falls back to neutral rather than guessing.
     */
    function statusTone(value) {
        switch (value) {
            case "PAID":
            case "FULFILLED":
                return "success";
            case "PENDING":
            case "AUTHORIZED":
            case "PARTIALLY_PAID":
            case "PARTIALLY_FULFILLED":
            case "IN_PROGRESS":
            case "SCHEDULED":
            case "ON_HOLD":
            case "PENDING_FULFILLMENT":
                return "warning";
            case "VOIDED":
            case "EXPIRED":
                return "error";
            default:
                return "neutral";
        }
    }
    function statusBadgeHtml(value) {
        var label = window.FlixHubFormat.humanizeStatus(value);
        if (!label)
            return "";
        var style = TONE_STYLE[statusTone(value)];
        return ('<span style="display:inline-flex;align-items:center;padding:3px 9px;border-radius:var(--radius-lg);font-size:11px;font-weight:700;' +
            style +
            '">' +
            window.FlixHubFormat.escapeHtml(label) +
            "</span>");
    }
    function thumbHtml(imageUrl, size) {
        if (imageUrl) {
            return ('<img src="' +
                window.FlixHubFormat.escapeHtml(imageUrl) +
                '" alt="" style="width:' +
                size +
                "px;height:" +
                size +
                'px;border-radius:10px;object-fit:cover;flex:none">');
        }
        return ('<span style="width:' +
            size +
            "px;height:" +
            size +
            'px;border-radius:10px;background:var(--surface2);display:flex;align-items:center;justify-content:center;flex:none;color:var(--faint)">' +
            PACKAGE_ICON +
            "</span>");
    }
    function orderRowHtml(hubEl, order, locale) {
        var title = order.primaryLineItemTitle || order.name;
        var date = window.FlixHubFormat.date(order.processedAt, locale);
        return ('<button type="button" data-flix-hub-nav="orders" data-flix-hub-order-id="' +
            window.FlixHubFormat.escapeHtml(order.id) +
            '" style="width:100%;display:flex;align-items:center;gap:14px;padding:14px 16px;border-radius:var(--radius-md);background:var(--surface);border:1px solid var(--border);cursor:pointer;text-align:start;transition:border-color .2s,transform .2s" style-hover="border-color:var(--borderHi);transform:translateY(-3px)">' +
            thumbHtml(order.primaryLineItemImageUrl, 48) +
            '<div style="min-width:0;flex:1">' +
            '<div style="font-size:14px;font-weight:700;color:var(--text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">' +
            window.FlixHubFormat.escapeHtml(title) +
            "</div>" +
            '<div style="font-size:12px;color:var(--dim);margin-top:4px">' +
            window.FlixHubFormat.escapeHtml(date) +
            "</div>" +
            "</div>" +
            // Price above status, end-aligned, as the reference stacks them —
            // and the same composition Overview's recent-order card uses, so a
            // shopper meets one order card, not two. Plain --text: the accent is
            // the Hub's navigation colour, and on the price it made every order
            // look like a link.
            '<div style="flex:none;display:flex;flex-direction:column;align-items:flex-end;gap:6px">' +
            '<div data-flix-order-price style="font-family:var(--display);font-weight:800;font-size:14px;color:var(--text)">' +
            window.FlixHubFormat.escapeHtml(window.FlixHubFormat.money(order.totalPrice, locale)) +
            "</div>" +
            '<div style="display:flex;align-items:center;flex-wrap:wrap;gap:6px;justify-content:flex-end">' +
            statusBadgeHtml(order.fulfillmentStatus) +
            statusBadgeHtml(order.financialStatus) +
            "</div>" +
            "</div>" +
            "</button>");
    }
    function loadMoreButtonHtml(hubEl, cursor) {
        return ('<button type="button" data-flix-orders-load-more data-cursor="' +
            window.FlixHubFormat.escapeHtml(cursor) +
            '" data-label-default="' +
            window.FlixHubFormat.escapeHtml(t(hubEl, "i18nLoadMore", "Load more")) +
            '" data-label-loading="' +
            window.FlixHubFormat.escapeHtml(t(hubEl, "i18nLoadingMore", "Loading…")) +
            '" style="width:100%;margin-top:4px;padding:12px 0;border-radius:var(--radius-button);border:1px solid var(--border);background:transparent;color:var(--text);font-family:var(--display);font-weight:700;font-size:13px;cursor:pointer" style-hover="border-color:var(--borderHi)">' +
            window.FlixHubFormat.escapeHtml(t(hubEl, "i18nLoadMore", "Load more")) +
            "</button>");
    }
    async function renderOrdersList(hubEl, contentEl) {
        contentEl.innerHTML = window.FlixHubRender.loadingState(t(hubEl, "i18nLoading", "Loading…"));
        var locale = document.documentElement.lang || "en";
        var page;
        try {
            page = await window.FlixAuthClient.listOrders({ limit: PAGE_LIMIT });
        }
        catch (err) {
            contentEl.innerHTML = window.FlixHubRender.errorState({
                title: t(hubEl, "i18nErrorTitle", "Something went wrong"),
                message: t(hubEl, "i18nErrorMessage", "Please try again."),
                retryLabel: t(hubEl, "i18nRetry", "Retry"),
            });
            return;
        }
        if (page.orders.length === 0) {
            renderEmptyOrders(hubEl, contentEl);
            return;
        }
        renderOrderRows(hubEl, contentEl, page.orders, page.pageInfo, locale);
    }
    function renderEmptyOrders(hubEl, contentEl) {
        contentEl.innerHTML = window.FlixHubRender.emptyState({
            title: t(hubEl, "i18nEmptyTitle", "No orders yet"),
            message: t(hubEl, "i18nEmptyMessage", "Your orders will show up here once you make a purchase."),
            iconSvg: PACKAGE_ICON,
            action: { label: t(hubEl, "i18nEmptyCta", "Browse the store"), href: "/" },
        });
    }
    function renderOrderRows(hubEl, contentEl, orders, pageInfo, locale) {
        var rowsHtml = orders
            .map(function (order) {
            return orderRowHtml(hubEl, order, locale);
        })
            .join("");
        var loadMore = pageInfo.hasNextPage && pageInfo.endCursor ? loadMoreButtonHtml(hubEl, pageInfo.endCursor) : "";
        // The section title. It sits above the list, not inside it, so the
        // load-more handler keeps appending into [data-flix-orders-list]
        // untouched. Same h2 the Email-preferences panel uses -- var(--display)
        // at 800/22px -- so the Hub has one heading voice rather than a second
        // one invented here. No text-align: the panel inherits its direction,
        // which is what puts it on the right in Arabic and the left in English.
        //
        // List view only. The empty, error and loading states are centred
        // compositions that carry their own title, and stacking a left-aligned
        // section heading over them would read as two competing titles; those
        // three states render exactly as they did before.
        contentEl.innerHTML =
            '<h2 style="font-family:var(--display);font-weight:800;font-size:22px;color:var(--text);margin:0 0 18px">' +
                window.FlixHubFormat.escapeHtml(t(hubEl, "i18nOrdersHeading", "Orders")) +
                "</h2>" +
                '<div data-flix-orders-list style="display:flex;flex-direction:column;gap:11px">' +
                rowsHtml +
                loadMore +
                "</div>";
    }
    async function onLoadMoreClick(hubEl, button) {
        var _a;
        var contentEl = button.closest("[data-flix-hub-content]");
        var listEl = (_a = button.closest("[data-flix-hub-content]")) === null || _a === void 0 ? void 0 : _a.querySelector("[data-flix-orders-list]");
        if (!contentEl || !listEl)
            return;
        var cursor = button.dataset.cursor;
        if (!cursor)
            return;
        button.setAttribute("disabled", "true");
        button.style.opacity = "0.6";
        button.style.cursor = "wait";
        button.textContent = button.dataset.labelLoading || "Loading…";
        var locale = document.documentElement.lang || "en";
        try {
            var page = await window.FlixAuthClient.listOrders({ limit: PAGE_LIMIT, cursor: cursor });
            var rowsHtml = page.orders
                .map(function (order) {
                return orderRowHtml(hubEl, order, locale);
            })
                .join("");
            button.insertAdjacentHTML("beforebegin", rowsHtml);
            // Rows injected outside renderCurrentPanel need their own
            // style-hover compilation pass (idempotent — see flix-hover.js).
            window.flixCompileHoverStyles(contentEl);
            if (page.pageInfo.hasNextPage && page.pageInfo.endCursor) {
                button.dataset.cursor = page.pageInfo.endCursor;
                button.removeAttribute("disabled");
                button.style.opacity = "";
                button.style.cursor = "pointer";
                button.textContent = button.dataset.labelDefault || "Load more";
            }
            else {
                button.remove();
            }
        }
        catch (err) {
            // A failed "load more" leaves the already-loaded rows intact — only
            // the button itself reverts, so the user doesn't lose what already
            // rendered successfully.
            button.removeAttribute("disabled");
            button.style.opacity = "";
            button.style.cursor = "pointer";
            button.textContent = button.dataset.labelDefault || "Load more";
        }
    }
    function orderDetailHtml(hubEl, order, locale) {
        var date = window.FlixHubFormat.date(order.processedAt, locale);
        var lineItemsHtml = order.lineItems
            .map(function (item) {
            return ('<div style="display:flex;align-items:center;gap:12px;padding:12px 0;border-bottom:1px solid var(--border)">' +
                thumbHtml(item.imageUrl, 44) +
                '<div style="min-width:0;flex:1">' +
                '<div style="font-size:14px;font-weight:600;color:var(--text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">' +
                window.FlixHubFormat.escapeHtml(item.title) +
                "</div>" +
                '<div style="font-size:12px;color:var(--dim);margin-top:2px">' +
                "×" +
                item.quantity +
                "</div>" +
                "</div>" +
                '<div style="flex:none;font-size:13px;font-weight:700;color:var(--text)">' +
                window.FlixHubFormat.escapeHtml(window.FlixHubFormat.money(item.totalPrice, locale)) +
                "</div>" +
                "</div>");
        })
            .join("");
        var totalsRows = [
            { label: t(hubEl, "i18nSubtotal", "Subtotal"), value: order.subtotalPrice },
            { label: t(hubEl, "i18nShipping", "Shipping"), value: order.totalShippingPrice },
            { label: t(hubEl, "i18nTax", "Tax"), value: order.totalTax },
        ]
            .filter(function (row) {
            return row.value !== null;
        })
            .map(function (row) {
            return ('<div style="display:flex;align-items:center;justify-content:space-between;font-size:13px;color:var(--dim)"><span>' +
                window.FlixHubFormat.escapeHtml(row.label) +
                "</span><span>" +
                window.FlixHubFormat.escapeHtml(window.FlixHubFormat.money(row.value, locale)) +
                "</span></div>");
        })
            .join("");
        return ('<div>' +
            '<button type="button" data-flix-hub-nav="orders" style="display:inline-flex;align-items:center;gap:8px;background:none;border:none;color:var(--dim);font-size:13px;font-weight:600;cursor:pointer;padding:0;margin-bottom:16px" style-hover="color:var(--text)">' +
            '<span aria-hidden="true" data-flix-hub-back-arrow style="display:flex">' +
            ARROW_BACK_ICON +
            "</span>" +
            window.FlixHubFormat.escapeHtml(t(hubEl, "i18nBackToOrders", "Back to orders")) +
            "</button>" +
            '<div style="display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;margin-bottom:6px">' +
            '<div style="font-family:var(--display);font-weight:800;font-size:19px;color:var(--text)">' +
            window.FlixHubFormat.escapeHtml(order.name) +
            "</div>" +
            '<div style="display:flex;gap:6px">' +
            statusBadgeHtml(order.fulfillmentStatus) +
            statusBadgeHtml(order.financialStatus) +
            "</div>" +
            "</div>" +
            '<div style="font-size:13px;color:var(--dim);margin-bottom:20px">' +
            window.FlixHubFormat.escapeHtml(date) +
            "</div>" +
            '<div style="border-radius:18px;background:var(--surface);border:1px solid var(--border);padding:6px 18px 18px">' +
            lineItemsHtml +
            '<div style="display:flex;flex-direction:column;gap:8px;padding-top:16px">' +
            totalsRows +
            '<div style="display:flex;align-items:center;justify-content:space-between;padding-top:10px;border-top:1px solid var(--border);font-family:var(--display);font-weight:800;font-size:16px;color:var(--text)"><span>' +
            window.FlixHubFormat.escapeHtml(t(hubEl, "i18nTotal", "Total")) +
            "</span><span>" +
            window.FlixHubFormat.escapeHtml(window.FlixHubFormat.money(order.totalPrice, locale)) +
            "</span></div>" +
            "</div>" +
            "</div>" +
            "</div>");
    }
    async function renderOrderDetail(hubEl, contentEl, orderId) {
        contentEl.innerHTML = window.FlixHubRender.loadingState(t(hubEl, "i18nLoading", "Loading…"));
        var locale = document.documentElement.lang || "en";
        try {
            var order = await window.FlixAuthClient.getOrderDetail(orderId);
            contentEl.innerHTML = orderDetailHtml(hubEl, order, locale);
        }
        catch (err) {
            if (isFlixApiError(err) && err.status === 404) {
                contentEl.innerHTML = window.FlixHubRender.emptyState({
                    title: t(hubEl, "i18nNotFoundTitle", "Order not found"),
                    message: t(hubEl, "i18nNotFoundMessage", "This order doesn't exist or isn't linked to your account."),
                    action: { label: t(hubEl, "i18nBackToOrders", "Back to orders"), dataAction: "data-flix-hub-nav=\"orders\"" },
                });
                return;
            }
            contentEl.innerHTML = window.FlixHubRender.errorState({
                title: t(hubEl, "i18nErrorTitle", "Something went wrong"),
                message: t(hubEl, "i18nErrorMessage", "Please try again."),
                retryLabel: t(hubEl, "i18nRetry", "Retry"),
            });
        }
    }
    function isFlixApiError(err) {
        return typeof err === "object" && err !== null && "status" in err && "code" in err;
    }
    async function render(hubEl, contentEl) {
        var orderId = orderIdFromLocation();
        if (orderId) {
            await renderOrderDetail(hubEl, contentEl, orderId);
            return;
        }
        await renderOrdersList(hubEl, contentEl);
    }
    function onClick(e) {
        var target = e.target;
        if (!target)
            return;
        var hubEl = document.getElementById("flix-account-hub-root");
        if (!hubEl)
            return;
        var loadMoreButton = target.closest("[data-flix-orders-load-more]");
        if (loadMoreButton && !loadMoreButton.hasAttribute("disabled")) {
            onLoadMoreClick(hubEl, loadMoreButton);
        }
    }
    if (!bound) {
        bound = true;
        document.addEventListener("click", onClick);
    }
    window.FlixHubOrders = { render: render };
})();
